import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, X, Upload, Package, Eye, Star, TrendingUp, Mail, MessageSquare, Send, Globe, Check, Zap, Settings, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { uploadToCloudinary } from '@/utils/cloudinary';
import { useAuth } from '@/contexts/AuthContext';

const CONTACT_TYPES = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'discord', label: 'Discord', icon: MessageSquare },
  { value: 'telegram', label: 'Telegram', icon: Send },
  { value: 'website', label: 'Website', icon: Globe },
];

const emptyListing = { game_id: '', title: '', description: '', image_urls: [], price: '', quantity: '', filter_values: {}, tags: '', listing_type: 'retail', min_quantity: '1', min_bid: '', instant_sell_price: '', auction_end_time: '' };
const ALL_CONTACT_TYPES = [
  { type: 'discord', placeholder: 'e.g. YourName#1234 or discord.gg/yourserver' },
  { type: 'telegram', placeholder: 'e.g. @YourTelegram' },
  { type: 'email', placeholder: 'e.g. you@email.com' },
  { type: 'website', placeholder: 'e.g. https://yoursite.com' },
];

export default function SellerDashboard() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState({});
  const [form, setForm] = useState(emptyListing);
  const [editId, setEditId] = useState(null);
  const [modal, setModal] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [contacts, setContacts] = useState(
    ALL_CONTACT_TYPES.map(ct => ({ type: ct.type, value: user?.contact_methods?.find(c => c.type === ct.type)?.value || '' }))
  );
  const [contactSaving, setContactSaving] = useState(false);
  const [bumpingId, setBumpingId] = useState(null);
  const [showRoleChange, setShowRoleChange] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [roleUsername, setRoleUsername] = useState(user?.username || '');
  const [roleContacts, setRoleContacts] = useState([{ type: 'discord', value: '' }]);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleUsernameCheck, setRoleUsernameCheck] = useState(null);
  const roleCheckTimeout = useRef(null);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    if (!user.email_verified) toast.warning('Please verify your email to create listings.');
    loadData();
  }, []);

  const loadData = async () => {
    const [l, g, s] = await Promise.allSettled([
      API.get('/dashboard/listings'),
      API.get('/games'),
      API.get('/dashboard/stats'),
    ]);
    if (l.status === 'fulfilled') setListings(l.value.data);
    if (g.status === 'fulfilled') setGames(g.value.data);
    if (s.status === 'fulfilled') setStats(s.value.data);
  };

  const openCreate = () => { setForm(emptyListing); setEditId(null); setSelectedGame(null); setModal(true); };
  const openEdit = (l) => {
    setForm({ ...l, tags: l.tags?.join(', ') || '', price: String(l.price), quantity: String(l.quantity), image_urls: l.image_urls || (l.image_url ? [l.image_url] : []) });
    setEditId(l.id);
    setSelectedGame(games.find(g => g.id === l.game_id) || null);
    setModal(true);
  };

  const handleGameChange = (gid) => {
    const g = games.find(g => g.id === gid);
    setSelectedGame(g || null);
    setForm(f => ({ ...f, game_id: gid, filter_values: {} }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgLoading(true);
    try {
      const urls = await Promise.all(files.map(f => uploadToCloudinary(f, 'listings')));
      setForm(f => ({ ...f, image_urls: [...(f.image_urls || []), ...urls].slice(0, 6) }));
      toast.success(`${urls.length} image(s) uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setImgLoading(false); }
  };

  const handlePasteUrl = (url) => {
    if (!url.trim()) return;
    try { new URL(url); } catch { toast.error('Invalid URL'); return; }
    setForm(f => ({ ...f, image_urls: [...(f.image_urls || []), url].slice(0, 6) }));
  };

  const removeImage = (i) => setForm(f => ({ ...f, image_urls: f.image_urls.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const needsPrice = form.listing_type !== 'auction' || !form.min_bid;
    if (!form.game_id || !form.title || !form.quantity) return toast.error('Please fill all required fields');
    if (form.listing_type !== 'auction' && !form.price) return toast.error('Please enter a price');
    if (form.listing_type === 'auction' && !form.min_bid) return toast.error('Please enter minimum bid');
    if (formLoading) return;
    setFormLoading(true);
    try {
      const payload = {
        game_id: form.game_id, title: form.title, description: form.description || '',
        image_url: form.image_urls?.[0] || '', image_urls: form.image_urls || [],
        price: form.listing_type === 'auction' ? parseFloat(form.min_bid || 0) : parseFloat(form.price),
        quantity: parseInt(form.quantity),
        filter_values: form.filter_values || {},
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        listing_type: form.listing_type || 'retail',
        min_quantity: form.listing_type === 'pack' ? parseInt(form.min_quantity || 1) : undefined,
        min_bid: form.listing_type === 'auction' ? parseFloat(form.min_bid) : undefined,
        instant_sell_price: form.listing_type === 'auction' && form.instant_sell_price ? parseFloat(form.instant_sell_price) : undefined,
        auction_end_time: form.listing_type === 'auction' && form.auction_end_time ? form.auction_end_time : undefined,
      };
      if (editId) {
        await API.put(`/listings/${editId}`, payload);
        toast.success('Listing updated!');
      } else {
        await API.post('/listings', payload);
        toast.success('Listing created!');
      }
      setModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save listing');
    } finally { setFormLoading(false); }
  };

  const handleBump = async (id) => {
    setBumpingId(id);
    try {
      const { data } = await API.post(`/listings/${id}/bump`);
      toast.success(data.message || 'Bumped to top!');
      const now = data.bumped_at;
      setListings(prev => prev.map(l => l.id === id ? { ...l, bumped_at: now } : l));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bump failed');
    } finally { setBumpingId(null); }
  };

  const canBump = (l) => {
    if (!l.bumped_at) return true;
    const last = new Date(l.bumped_at);
    return (Date.now() - last.getTime()) >= 86400000;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this listing?')) return;
    try {
      await API.delete(`/listings/${id}`);
      setListings(prev => prev.filter(l => l.id !== id));
      toast.success('Listing removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete listing');
    }
  };

  const handleToggleActive = async (l) => {
    try {
      await API.put(`/listings/${l.id}`, { is_active: !l.is_active });
      setListings(prev => prev.map(li => li.id === l.id ? { ...li, is_active: !li.is_active } : li));
      toast.success(l.is_active ? 'Listing deactivated' : 'Listing activated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update listing');
    }
  };

  const handleSaveContacts = async () => {
    const valid = contacts.filter(c => c.value.trim());
    if (valid.length === 0) return toast.error('Must have at least one contact method');
    setContactSaving(true);
    try {
      await API.put('/auth/update-contacts', { contact_methods: valid });
      updateUser({ contact_methods: valid });
      toast.success('Contact methods saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setContactSaving(false); }
  };

  const checkRoleUsername = (val) => {
    if (roleCheckTimeout.current) clearTimeout(roleCheckTimeout.current);
    if (!val || val.length < 3 || newRole !== 'seller') { setRoleUsernameCheck(null); return; }
    setRoleUsernameCheck('checking');
    roleCheckTimeout.current = setTimeout(async () => {
      try {
        const { data } = await API.get(`/auth/check-username?username=${encodeURIComponent(val)}&role=seller`);
        setRoleUsernameCheck(data.available ? 'available' : 'taken');
      } catch { setRoleUsernameCheck(null); }
    }, 400);
  };

  const handleChangeRole = async () => {
    if (!newRole) return toast.error('Please select a new account type');
    if (newRole === user?.role) return toast.error(`You are already a ${newRole}`);
    if (newRole === 'seller') {
      if (roleUsernameCheck === 'taken') return toast.error('Username already taken');
      const validContacts = roleContacts.filter(c => c.value.trim());
      if (validContacts.length === 0) return toast.error('Sellers must provide at least one contact method');
    }
    setRoleSaving(true);
    try {
      const { data } = await API.post('/auth/change-role', {
        new_role: newRole,
        username: newRole === 'seller' ? roleUsername : undefined,
        contact_methods: newRole === 'seller' ? roleContacts.filter(c => c.value.trim()) : [],
      });
      updateUser(data.user);
      toast.success(data.message);
      setShowRoleChange(false);
      if (newRole === 'buyer') navigate('/browse');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    } finally { setRoleSaving(false); }
  };

  const updateRoleContact = (type, val) => {
    setRoleContacts(prev => {
      const idx = prev.findIndex(c => c.type === type);
      if (idx >= 0) {
        const updated = [...prev];
        if (!val.trim()) updated.splice(idx, 1);
        else updated[idx] = { type, value: val };
        return updated.length ? updated : [{ type: 'discord', value: '' }];
      }
      return val.trim() ? [...prev, { type, value: val }] : prev;
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-rajdhani font-bold text-3xl uppercase">
          Seller <span className="text-rm-red">Dashboard</span>
        </h1>
        {!user?.email_verified && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-sm text-xs text-yellow-500 flex items-center gap-2">
            Email not verified. Some features are restricted.
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Listings', val: stats.total_listings || 0, icon: Package },
          { label: 'Active', val: stats.active_listings || 0, icon: TrendingUp },
          { label: 'Contact Reveals', val: stats.total_reveals || 0, icon: Eye },
          { label: 'Positive Reviews', val: `${stats.positive_percent || 0}%`, icon: Star },
        ].map(({ label, val, icon: Icon }) => (
          <div key={label} className="bg-rm-card border border-rm-border rounded-sm p-4">
            <Icon size={18} className="text-rm-red mb-2" />
            <p className="font-rajdhani font-bold text-2xl text-white">{val}</p>
            <p className="text-xs text-rm-muted uppercase tracking-widest">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-rm-border mb-6">
        {['listings', 'contacts', 'settings'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-6 py-3 font-rajdhani font-bold uppercase tracking-widest text-sm transition-colors
              ${tab === t ? 'text-rm-red border-b-2 border-rm-red -mb-[2px]' : 'text-rm-muted hover:text-white'}`}>
            {t === 'listings' ? 'My Listings' : t === 'contacts' ? 'Contact Settings' : 'Account Settings'}
          </button>
        ))}
      </div>

      {/* LISTINGS TAB */}
      {tab === 'listings' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={openCreate} data-testid="create-listing-btn"
              className="btn-primary px-6 py-3 rounded-sm text-sm flex items-center gap-2">
              <Plus size={16} /> New Listing
            </button>
          </div>
          {listings.length === 0 ? (
            <div className="bg-rm-card border border-rm-border rounded-sm p-12 text-center text-rm-muted">
              <Package size={40} className="mx-auto mb-3 text-zinc-700" />
              <p className="font-rajdhani font-bold text-xl uppercase mb-2">No Listings Yet</p>
              <p className="text-sm mb-4">Create your first listing to start selling</p>
              <button onClick={openCreate} className="btn-primary px-6 py-3 rounded-sm text-sm">Create Listing</button>
            </div>
          ) : (
            <div className="space-y-3">
              {listings.map(l => (
                <div key={l.id} className="bg-rm-card border border-rm-border rounded-sm p-4 flex items-center gap-4 card-hover" data-testid={`dashboard-listing-${l.id}`}>
                  {l.image_url && <img src={l.image_url} alt={l.title} className="w-14 h-14 object-cover rounded-sm border border-rm-border" />}
                  {!l.image_url && <div className="w-14 h-14 bg-rm-surface rounded-sm border border-rm-border flex items-center justify-center"><Package size={20} className="text-zinc-700" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-rajdhani font-bold text-white uppercase truncate">{l.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-rm-red font-bold text-sm">${l.price}</span>
                      <span className="text-xs text-rm-muted">Qty: {l.quantity}</span>
                      <span className="text-xs text-rm-muted">{l.game_name}</span>
                      <span className={`text-xs ${l.is_active && l.quantity > 0 ? 'text-green-500' : 'text-zinc-600'}`}>
                        {l.is_active && l.quantity > 0 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleBump(l.id)} disabled={!canBump(l) || bumpingId === l.id}
                      title={canBump(l) ? 'Bump to top' : 'Already bumped (24h cooldown)'}
                      data-testid={`bump-listing-${l.id}`}
                      className={`p-2 transition-colors rounded-sm ${canBump(l) ? 'text-rm-muted hover:text-rm-red' : 'text-zinc-700 cursor-not-allowed'}`}>
                      {bumpingId === l.id
                        ? <div className="w-4 h-4 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
                        : <Zap size={16} />}
                    </button>
                    <button onClick={() => handleToggleActive(l)} title={l.is_active ? 'Deactivate' : 'Activate'}
                      className="p-2 text-rm-muted hover:text-green-500 transition-colors" data-testid={`toggle-listing-${l.id}`}>
                      <Check size={16} />
                    </button>
                    <button onClick={() => openEdit(l)} className="p-2 text-rm-muted hover:text-white transition-colors" data-testid={`edit-listing-${l.id}`}>
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDelete(l.id)} className="p-2 text-rm-muted hover:text-rm-red transition-colors" data-testid={`delete-listing-${l.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CONTACTS TAB */}
      {tab === 'contacts' && (
        <div className="max-w-lg">
          <p className="text-sm text-rm-muted mb-5">These contact methods will be shown to buyers when they reveal your contact info. Fill in at least one.</p>
          <div className="space-y-4">
            {ALL_CONTACT_TYPES.map(({ type, placeholder }) => (
              <div key={type} className="flex items-center gap-3">
                <div className="w-24 shrink-0">
                  <span className="text-sm text-rm-muted capitalize font-medium">{type}</span>
                </div>
                <input type="text" placeholder={placeholder}
                  value={contacts.find(c => c.type === type)?.value || ''}
                  onChange={e => setContacts(prev => prev.map(c => c.type === type ? { ...c, value: e.target.value } : c))}
                  className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2.5 rounded-sm text-sm" />
              </div>
            ))}
          </div>
          <button onClick={handleSaveContacts} disabled={contactSaving} data-testid="save-contacts-btn"
            className="btn-primary px-8 py-3 text-sm rounded-sm disabled:opacity-50 mt-6">
            {contactSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* SETTINGS TAB */}
      {tab === 'settings' && (
        <div className="max-w-lg">
          <div className="bg-rm-card border border-rm-border rounded-sm p-6">
            <h3 className="font-rajdhani font-bold text-lg uppercase tracking-wide mb-1 flex items-center gap-2">
              <ArrowRightLeft size={18} className="text-rm-red" /> Change Account Type
            </h3>
            <p className="text-sm text-rm-muted mb-5">
              Currently: <span className={`font-bold uppercase ${user?.role === 'seller' ? 'text-rm-red' : 'text-blue-400'}`}>{user?.role}</span>
            </p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {['buyer', 'seller'].map(r => (
                <button key={r} type="button"
                  onClick={() => { setNewRole(r); setRoleUsername(user?.username || ''); checkRoleUsername(user?.username || '', r); }}
                  disabled={r === user?.role}
                  data-testid={`change-role-${r}`}
                  className={`py-3 border rounded-sm font-rajdhani font-bold uppercase text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed
                    ${newRole === r
                      ? 'border-rm-red text-rm-red bg-rm-red/10'
                      : 'border-rm-border text-rm-muted hover:border-white/20 hover:text-white'}`}>
                  {r}
                  {r === user?.role && ' (Current)'}
                </button>
              ))}
            </div>

            {newRole && newRole !== user?.role && (
              <div className="space-y-4 border-t border-rm-border pt-4">
                {newRole === 'seller' && (
                  <>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">
                        Unique Seller Username <span className="text-rm-red">*</span>
                      </label>
                      <div className="relative">
                        <input type="text" value={roleUsername}
                          onChange={e => { setRoleUsername(e.target.value); checkRoleUsername(e.target.value, 'seller'); }}
                          placeholder="unique_username"
                          data-testid="role-change-username"
                          className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2.5 rounded-sm text-sm pr-10" />
                        <div className="absolute right-3 top-2.5">
                          {roleUsernameCheck === 'checking' && <div className="w-4 h-4 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" />}
                          {roleUsernameCheck === 'available' && <Check size={14} className="text-green-500" />}
                          {roleUsernameCheck === 'taken' && <X size={14} className="text-rm-red" />}
                        </div>
                      </div>
                      {roleUsernameCheck === 'taken' && <p className="text-[10px] text-rm-red mt-1">Username taken</p>}
                      {roleUsernameCheck === 'available' && <p className="text-[10px] text-green-500 mt-1">Available!</p>}
                      <p className="text-[10px] text-zinc-600 mt-1">Seller usernames must be unique across all sellers</p>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Contact Methods (at least one)</label>
                      <div className="space-y-2">
                        {ALL_CONTACT_TYPES.map(({ type, placeholder }) => (
                          <div key={type} className="flex items-center gap-3">
                            <span className="text-xs text-rm-muted capitalize w-20 shrink-0">{type}</span>
                            <input type="text" placeholder={placeholder}
                              value={roleContacts.find(c => c.type === type)?.value || ''}
                              onChange={e => updateRoleContact(type, e.target.value)}
                              className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {newRole === 'buyer' && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-4">
                    <p className="text-sm text-amber-400 font-medium mb-1">Switching to Buyer</p>
                    <p className="text-xs text-zinc-500">Your listings will remain but you won't be able to create new ones. You can switch back to seller anytime.</p>
                  </div>
                )}
                <button onClick={handleChangeRole} disabled={roleSaving || (newRole === 'seller' && roleUsernameCheck === 'taken')}
                  data-testid="confirm-role-change-btn"
                  className="w-full btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
                  {roleSaving ? 'Changing...' : `Switch to ${newRole}`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="listing-modal">
          <div className="bg-rm-card border border-rm-border rounded-sm w-full max-w-lg my-8 animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-rm-border">
              <h3 className="font-rajdhani font-bold text-xl uppercase">{editId ? 'Edit Listing' : 'New Listing'}</h3>
              <button onClick={() => setModal(false)} className="text-rm-muted hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Game */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Game *</label>
                <select value={form.game_id} onChange={e => handleGameChange(e.target.value)} required
                  className="w-full bg-rm-bg border border-rm-border text-white text-sm px-3 py-2 rounded-sm" data-testid="listing-game">
                  <option value="">Select a game...</option>
                  {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              {/* Game-specific filters */}
              {selectedGame?.filters?.map(f => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">{f.name}</label>
                  {f.type === 'select' ? (
                    <select value={form.filter_values[f.name] || ''}
                      onChange={e => setForm(fm => ({ ...fm, filter_values: { ...fm.filter_values, [f.name]: e.target.value } }))}
                      className="w-full bg-rm-bg border border-rm-border text-white text-sm px-3 py-2 rounded-sm">
                      <option value="">Any</option>
                      {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={form.filter_values[f.name] || ''}
                      onChange={e => setForm(fm => ({ ...fm, filter_values: { ...fm.filter_values, [f.name]: e.target.value } }))}
                      placeholder={f.name}
                      className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" />
                  )}
                </div>
              ))}
              {/* Title */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
                  placeholder="Item name" className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm"
                  data-testid="listing-title" />
              </div>
              {/* Description */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Item details..." rows={3}
                  className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm resize-none" />
              </div>
              {/* Listing Type */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Listing Type *</label>
                <div className="grid grid-cols-3 gap-2" data-testid="listing-type-selector">
                  {[
                    { value: 'retail', label: 'Single', desc: 'Standard listing' },
                    { value: 'pack', label: 'Pack / Bulk', desc: 'Bundle of items' },
                    { value: 'auction', label: 'Auction', desc: 'Highest bidder wins' },
                  ].map(t => (
                    <button key={t.value} type="button"
                      data-testid={`type-btn-${t.value}`}
                      onClick={() => setForm(f => ({ ...f, listing_type: t.value }))}
                      className={`p-3 rounded-sm border text-left transition-all duration-150 ${
                        form.listing_type === t.value
                          ? t.value === 'auction' ? 'border-amber-500 bg-amber-500/10 text-amber-300'
                          : t.value === 'pack' ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                          : 'border-rm-red bg-rm-red/10 text-white'
                          : 'border-rm-border text-rm-muted hover:border-zinc-600'
                      }`}>
                      <div className="text-xs font-rajdhani font-bold uppercase tracking-wide">{t.label}</div>
                      <div className="text-[10px] opacity-60 mt-0.5">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {/* Price & Qty */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">
                    {form.listing_type === 'auction' ? 'Min Bid ($) *' : 'Price ($) *'}
                  </label>
                  <input type="number"
                    value={form.listing_type === 'auction' ? form.min_bid : form.price}
                    onChange={e => setForm(f => form.listing_type === 'auction' ? { ...f, min_bid: e.target.value } : { ...f, price: e.target.value })}
                    required min="0" step="0.01"
                    placeholder={form.listing_type === 'auction' ? 'Starting bid' : 'Price'}
                    className="w-full bg-rm-bg border border-rm-border text-white px-3 py-2 rounded-sm text-sm" data-testid="listing-price" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Quantity *</label>
                  <input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} required min="1"
                    className="w-full bg-rm-bg border border-rm-border text-white px-3 py-2 rounded-sm text-sm" data-testid="listing-quantity" />
                </div>
              </div>
              {/* Pack: min quantity */}
              {form.listing_type === 'pack' && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Min Quantity per Order</label>
                  <input type="number" value={form.min_quantity} onChange={e => setForm(f => ({ ...f, min_quantity: e.target.value }))} min="1"
                    placeholder="Set to 1 for single pack"
                    className="w-full bg-rm-bg border border-blue-500/30 text-white px-3 py-2 rounded-sm text-sm" data-testid="listing-min-qty" />
                  <p className="text-[10px] text-zinc-600 mt-1">Set to 1 if you are selling a single pack</p>
                </div>
              )}
              {/* Auction: instant sell + end time */}
              {form.listing_type === 'auction' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Instant Sell Price ($)</label>
                    <input type="number" value={form.instant_sell_price} onChange={e => setForm(f => ({ ...f, instant_sell_price: e.target.value }))} min="0" step="0.01"
                      placeholder="Optional buy-now price"
                      className="w-full bg-rm-bg border border-amber-500/30 text-white px-3 py-2 rounded-sm text-sm" data-testid="listing-instant-sell" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">End Date (optional)</label>
                    <input type="datetime-local" value={form.auction_end_time} onChange={e => setForm(f => ({ ...f, auction_end_time: e.target.value }))}
                      className="w-full bg-rm-bg border border-amber-500/30 text-white px-3 py-2 rounded-sm text-sm" data-testid="listing-auction-end" />
                  </div>
                </div>
              )}
              {/* Tags */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Tags (comma-separated)</label>
                <input type="text" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="rare, limited, og..."
                  className="w-full bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-sm" />
              </div>
              {/* Images */}
              <div>
                <label className="text-xs uppercase tracking-widest text-rm-muted block mb-2">Item Images <span className="text-zinc-600 normal-case tracking-normal">(optional, up to 6)</span></label>
                {/* Thumbnails */}
                {form.image_urls?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {form.image_urls.map((url, i) => (
                      <div key={i} className="relative w-16 h-16">
                        <img src={url} alt={`img-${i}`} className="w-full h-full object-cover rounded-sm border border-rm-border" />
                        <button type="button" onClick={() => removeImage(i)}
                          className="absolute -top-1.5 -right-1.5 bg-rm-bg border border-rm-border rounded-full p-0.5 text-rm-muted hover:text-rm-red">
                          <X size={10} />
                        </button>
                        {i === 0 && <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] bg-rm-red/80 text-white">Main</span>}
                      </div>
                    ))}
                  </div>
                )}
                {/* Upload button */}
                {(!form.image_urls || form.image_urls.length < 6) && (
                  <div className="space-y-2">
                    <label className="flex items-center justify-center gap-2 border border-dashed border-rm-border rounded-sm p-3 cursor-pointer hover:border-rm-red/50 transition-colors"
                      data-testid="image-upload">
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                      {imgLoading ? <div className="w-5 h-5 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
                        : <><Upload size={16} className="text-rm-muted" /><span className="text-xs text-rm-muted">Upload Images (select multiple)</span></>}
                    </label>
                    {/* Paste URL */}
                    <div className="flex gap-2">
                      <input type="text" placeholder="Or paste image URL here..."
                        className="flex-1 bg-rm-bg border border-rm-border text-white placeholder:text-zinc-600 px-3 py-2 rounded-sm text-xs"
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePasteUrl(e.target.value); e.target.value = ''; }}}
                        data-testid="paste-url-input" />
                      <button type="button"
                        onClick={e => { const inp = e.target.previousSibling; handlePasteUrl(inp.value); inp.value = ''; }}
                        className="btn-outline px-3 py-1 text-xs rounded-sm">Add</button>
                    </div>
                    <p className="text-[10px] text-zinc-600">Press Enter or click Add to paste URL. First image is the main display image.</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="flex-1 btn-outline py-3 rounded-sm text-sm">Cancel</button>
                <button type="submit" disabled={formLoading} data-testid="submit-listing-btn"
                  className="flex-1 btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
                  {formLoading ? 'Saving...' : editId ? 'Update Listing' : 'Create Listing'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
